import core, { App, Entities, View } from '@gin-betting/core';
import { difference, reduce, transform, uniq } from 'lodash';

import { State } from 'types';
import { TRAVERSE_STATE_KEYS, TraverseStateKeys } from './constants';
import { betHistoryLocator, matchLocators } from './locators';
import gcReserve from './reserve';

const {
    utils: { splitMarketPath, getIdsFromOddId },
    entities: {
        utils: { renormalize },
        selectors: { selectOddsStatistic, selectOddsStatisticMarkets },
    },
} = core;

export const locateSweepableMatches = (
    state: State
): Entities.DeleteEntitiesPayload | void => {
    const entitiesState = state.get('entities');
    const cachedMatchIds = entitiesState.get('matches').keySeq().toArray();

    const sweepableMatchIds = difference(
        cachedMatchIds,
        locateReachableMatchIds(state)
    );

    if (sweepableMatchIds.length === 0) return;

    const renormalized = renormalize(
        App.EntityNames.Matches,
        sweepableMatchIds,
        entitiesState
    );

    if (!renormalized) return;

    const renormalizedToIds = transformRenormalizedToIds(renormalized.entities);

    const oddsStatistic = selectOddsStatistic(state)
        .filter(({ id: oddId }) => {
            const matchId = getIdsFromOddId(oddId)?.matchId;

            return !matchId || renormalized.entities.matches[matchId];
        })
        .keySeq()
        .toArray();

    const oddsStatisticMarkets = selectOddsStatisticMarkets(state)
        .filter(({ id: marketId }) => {
            const matchId = splitMarketPath(marketId)?.matchId;

            return !matchId || renormalized.entities.matches[matchId];
        })
        .keySeq()
        .toArray();

    return { ...renormalizedToIds, oddsStatistic, oddsStatisticMarkets };
};

const transformRenormalizedToIds = (
    entities: View.NormalizedEntities
): Entities.DeleteEntitiesPayload => {
    return transform(entities, (result, entitiesCollection, key) => {
        let value = entitiesCollection;

        if (key === 'tournaments') return result;
        if (key === 'slugs') {
            value = transform(value, (acc, slug: View.Slug, slugId) => {
                if (slug.matchId) {
                    acc[slugId] = slug;
                }

                return acc;
            });
        }

        result[key] = Object.keys(value);

        return result;
    });
};

export const locateReachableMatchIds = (state: State): string[] => {
    const reachableMatchIdsFromState = reduce(
        TRAVERSE_STATE_KEYS,
        (acc, key: TraverseStateKeys) => {
            const locateReachableMatches = matchLocators[key];

            // NOTE: async modules can be nullable. TODO: Improve "State" type GINFE-1830
            const stateTree: State[typeof key] | null = state.get(key);

            if (!stateTree) return acc;

            const reachableMatchIds = locateReachableMatches(
                stateTree as UnionToIntersection<typeof stateTree>,
                state.get('entities')
            );

            return acc.concat(reachableMatchIds);
        },
        [...betHistoryLocator(state)] as string[]
    );

    return uniq(reachableMatchIdsFromState.concat(gcReserve.get('matches')));
};
